import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDeals } from "../hooks/useApi";
import DynamicFormRenderer from "../components/forms/DynamicFormRenderer";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import ErrorDisplay from "../components/ui/ErrorDisplay";
import { StatusBadge } from "../components/ui/StatusIndicators";
import {
  TrashIcon,
  PencilIcon,
  EyeIcon,
  HomeIcon,
  ChevronRightIcon,
  CurrencyPoundIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CommandLineIcon,
  PlayIcon,
  LinkIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  getAutomations,
  triggerAutomation,
  attachAutomationToDeal,
  detachAutomationFromDeal,
} from "../services/api";

function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    items: deals,
    loading,
    error,
    updateItem,
    deleteItem,
    refresh,
  } = useDeals();

  const [deal, setDeal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);

  // Automation management state
  const [availableAutomations, setAvailableAutomations] = useState([]);
  const [showAutomationModal, setShowAutomationModal] = useState(false);
  const [loadingAutomations, setLoadingAutomations] = useState(false);

  // Find the deal
  useEffect(() => {
    if (deals && id) {
      const foundDeal = deals.find((d) => d.id === id);
      setDeal(foundDeal);

      if (foundDeal) {
        setFormData(transformDealToFormData(foundDeal));
      }
    }
  }, [deals, id]);

  // Transform deal data from API format to form format
  const transformDealToFormData = (deal) => {
    if (!deal) return {};

    return {
      // Basic Information
      deal_name: deal.name || "",
      deal_type: deal.type || "",
      deal_status: deal.status || "",
      deal_structure: deal.dealStructure || "",
      deal_description: deal.description || "",
      related_project: deal.relatedProject || "",

      // Deal Parties
      lead_party_name: deal.parties?.lead?.name || "",
      lead_party_role: deal.parties?.lead?.role || "",
      lead_party_equity: deal.parties?.lead?.equity || "",
      lead_party_contact: deal.parties?.lead?.contact || "",
      partner_party_name: deal.parties?.partner?.name || "",
      partner_party_role: deal.parties?.partner?.role || "",
      partner_party_equity: deal.parties?.partner?.equity || "",
      partner_party_contact: deal.parties?.partner?.contact || "",

      // Financial Details
      total_deal_value: deal.financial?.totalDealValue || "",
      land_cost: deal.financial?.landCost || "",
      development_cost: deal.financial?.developmentCost || "",
      expected_return: deal.financial?.expectedReturn || "",
      irr: deal.financial?.irr || "",
      payback_period: deal.financial?.paybackPeriod || "",
      risk_profile: deal.financial?.riskProfile || "",

      // Terms & Conditions
      agreement_date: deal.terms?.agreementDate || "",
      completion_date: deal.terms?.completionDate || "",
      profit_split: deal.terms?.profitSplit || "",
      management_fee: deal.terms?.managementFee || "",
      preferred_return: deal.terms?.preferredReturn || "",
      hurdle_rate: deal.terms?.hurdle || "",
    };
  };

  // Transform form data back to API format
  const transformFormDataToDeal = (formData) => {
    return {
      name: formData["deal_name"] || "",
      type: formData["deal_type"] || "",
      status: formData["deal_status"] || "",
      dealStructure: formData["deal_structure"] || "",
      description: formData["deal_description"] || "",
      relatedProject: formData["related_project"] || "",
      parties: {
        lead: {
          name: formData["lead_party_name"] || "",
          role: formData["lead_party_role"] || "",
          equity: parseFloat(formData["lead_party_equity"]) || 0,
          contact: formData["lead_party_contact"] || "",
        },
        partner: {
          name: formData["partner_party_name"] || "",
          role: formData["partner_party_role"] || "",
          equity: parseFloat(formData["partner_party_equity"]) || 0,
          contact: formData["partner_party_contact"] || "",
        },
      },
      financial: {
        totalDealValue: parseFloat(formData["total_deal_value"]) || 0,
        landCost: parseFloat(formData["land_cost"]) || 0,
        developmentCost: parseFloat(formData["development_cost"]) || 0,
        expectedReturn: parseFloat(formData["expected_return"]) || 0,
        irr: parseFloat(formData["irr"]) || 0,
        paybackPeriod: parseInt(formData["payback_period"]) || 0,
        riskProfile: formData["risk_profile"] || "",
      },
      terms: {
        agreementDate: formData["agreement_date"] || "",
        completionDate: formData["completion_date"] || "",
        profitSplit: formData["profit_split"] || "",
        managementFee: parseFloat(formData["management_fee"]) || 0,
        preferredReturn: parseFloat(formData["preferred_return"]) || 0,
        hurdle: parseFloat(formData["hurdle_rate"]) || 0,
      },
    };
  };

  const handleSave = async (data) => {
    try {
      const updatedDeal = transformFormDataToDeal(data);
      await updateItem(id, updatedDeal);
      setIsEditing(false);
      // Refresh the deal data
      if (deals) {
        const refreshedDeal = deals.find((d) => d.id === id);
        if (refreshedDeal) {
          setDeal({ ...refreshedDeal, ...updatedDeal });
        }
      }
      refresh(); // Refresh to get updated data
    } catch (error) {
      console.error("Failed to save deal:", error);
      alert("Failed to save deal. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm(
        `Are you sure you want to delete "${deal.name}"? This action cannot be undone.`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteItem(id);
        navigate("/deals");
      } catch (error) {
        console.error("Failed to delete deal:", error);
        alert("Failed to delete deal. Please try again.");
        setIsDeleting(false);
      }
    }
  };

  // Automation management functions
  const loadAvailableAutomations = async () => {
    setLoadingAutomations(true);
    try {
      const response = await getAutomations();
      setAvailableAutomations(response.automations || []);
    } catch (error) {
      console.error("Failed to load automations:", error);
    } finally {
      setLoadingAutomations(false);
    }
  };

  const handleShowAutomations = () => {
    setShowAutomationModal(true);
    loadAvailableAutomations();
  };

  const handleCloseAutomationModal = () => {
    setShowAutomationModal(false);
  };

  const handleTriggerAutomation = async (automationId, automationName) => {
    try {
      await triggerAutomation(automationId);
      alert(`Successfully triggered "${automationName}" for this deal!`);
      refresh(); // Reload deal to show updated trigger counts
    } catch (error) {
      alert(`Failed to trigger automation: ${error.message}`);
    }
  };

  const handleAttachAutomation = async (automationId) => {
    try {
      await attachAutomationToDeal(id, automationId);
      alert("Automation attached successfully!");
      refresh(); // Reload deal to show the new attachment
      loadAvailableAutomations(); // Refresh available automations
    } catch (error) {
      alert(`Failed to attach automation: ${error.message}`);
    }
  };

  const handleDetachAutomation = async (automationId) => {
    if (window.confirm("Are you sure you want to detach this automation?")) {
      try {
        await detachAutomationFromDeal(id, automationId);
        alert("Automation detached successfully!");
        refresh(); // Reload deal to reflect the change
        loadAvailableAutomations(); // Refresh available automations
      } catch (error) {
        alert(`Failed to detach automation: ${error.message}`);
      }
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "Not specified";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDealTypeLabel = (type) => {
    const labels = {
      "joint-venture": "Joint Venture",
      acquisition: "Acquisition",
      "development-finance": "Development Finance",
      "forward-sale": "Forward Sale",
      refinancing: "Refinancing",
    };
    return labels[type] || type?.charAt(0).toUpperCase() + type?.slice(1);
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="large" message="Loading deal..." />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="py-12">
        <ErrorDisplay
          error={error}
          message="Failed to load deal"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  // Handle deal not found
  if (!loading && !deal) {
    return (
      <div className="py-12">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Deal Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            The deal with ID "{id}" could not be found.
          </p>
          <Link to="/deals" className="btn-primary">
            ← Back to Deals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2 text-sm text-gray-500">
          <li>
            <Link to="/" className="hover:text-gray-700">
              <HomeIcon className="w-4 h-4" />
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li>
            <Link to="/deals" className="hover:text-gray-700">
              Deals
            </Link>
          </li>
          <ChevronRightIcon className="w-4 h-4" />
          <li className="text-gray-900 font-medium truncate">{deal.name}</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 truncate">
            {deal.name}
          </h1>
          <div className="mt-2 flex items-center space-x-4">
            <StatusBadge status={deal.status} />
            <span className="text-sm text-gray-500">
              {getDealTypeLabel(deal.type)}
            </span>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          {!isEditing ? (
            <>
              <button onClick={handleShowAutomations} className="btn-secondary">
                <CommandLineIcon className="w-4 h-4 mr-2" />
                Automations
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="btn-secondary"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-danger"
              >
                <TrashIcon className="w-4 h-4 mr-2" />
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(false)}
              className="btn-secondary"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Mode
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {!isEditing && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center">
              <CurrencyPoundIcon className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Value</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(deal.financial?.totalDealValue)}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <BuildingOfficeIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  Expected Return
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {deal.financial?.expectedReturn
                    ? `${deal.financial.expectedReturn}%`
                    : "Not specified"}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <CalendarIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">
                  Completion Date
                </p>
                <p className="text-lg font-semibold text-gray-900">
                  {deal.terms?.completionDate
                    ? new Date(deal.terms.completionDate).toLocaleDateString()
                    : "Not specified"}
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <CommandLineIcon className="w-8 h-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Automations</p>
                <p className="text-lg font-semibold text-gray-900">
                  {deal.attachedAutomations?.length || 0} attached
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attached Automations (when not editing) */}
      {!isEditing &&
        deal.attachedAutomations &&
        deal.attachedAutomations.length > 0 && (
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Attached Automations
            </h3>
            <div className="space-y-3">
              {deal.attachedAutomations.map((automation) => (
                <div
                  key={automation.automationId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <CommandLineIcon className="h-5 w-5 text-indigo-600" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {automation.automationName}
                      </p>
                      <p className="text-sm text-gray-600">
                        Status: {automation.status} • Triggered{" "}
                        {automation.triggerCount} times
                        {automation.lastTriggered && (
                          <span>
                            {" "}
                            • Last:{" "}
                            {new Date(
                              automation.lastTriggered
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {automation.status === "active" && (
                      <button
                        onClick={() =>
                          handleTriggerAutomation(
                            automation.automationId,
                            automation.automationName
                          )
                        }
                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                        title="Trigger automation"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Form */}
      <div className="card">
        {Object.keys(formData).length > 0 ? (
          <DynamicFormRenderer
            key={`deal-${id}-${isEditing ? "edit" : "view"}`}
            templateId="deal-basic-v1"
            fieldsId="deal-fields"
            data={formData}
            onDataChange={setFormData}
            onSave={handleSave}
            readOnly={!isEditing}
            autoSave={false}
            showProgress={true}
          />
        ) : (
          <div className="flex justify-center py-8">
            <LoadingSpinner message="Loading deal data..." />
          </div>
        )}
      </div>

      {/* Automation Management Modal */}
      {showAutomationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Manage Automations
                </h2>
                <p className="text-sm text-gray-600">{deal.name}</p>
              </div>
              <button
                onClick={handleCloseAutomationModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
              {/* Currently Attached Automations */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-900 mb-3">
                  Currently Attached ({deal.attachedAutomations?.length || 0})
                </h3>
                {deal.attachedAutomations?.length > 0 ? (
                  <div className="space-y-3">
                    {deal.attachedAutomations.map((automation) => (
                      <div
                        key={automation.automationId}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <CommandLineIcon className="h-5 w-5 text-indigo-600" />
                          <div>
                            <p className="font-medium text-gray-900">
                              {automation.automationName}
                            </p>
                            <p className="text-sm text-gray-600">
                              Status: {automation.status} • Triggered{" "}
                              {automation.triggerCount} times
                              {automation.lastTriggered && (
                                <span>
                                  {" "}
                                  • Last:{" "}
                                  {new Date(
                                    automation.lastTriggered
                                  ).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {automation.status === "active" && (
                            <button
                              onClick={() =>
                                handleTriggerAutomation(
                                  automation.automationId,
                                  automation.automationName
                                )
                              }
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                              title="Trigger automation"
                            >
                              <PlayIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() =>
                              handleDetachAutomation(automation.automationId)
                            }
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Detach automation"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">
                    No automations currently attached to this deal.
                  </p>
                )}
              </div>

              {/* Available Automations to Attach */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">
                  Available Automations
                </h3>
                {loadingAutomations ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner size="small" />
                    <span className="ml-2 text-gray-600">
                      Loading automations...
                    </span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableAutomations
                      .filter(
                        (automation) =>
                          !deal.attachedAutomations?.some(
                            (attached) =>
                              attached.automationId === automation.id
                          )
                      )
                      .map((automation) => (
                        <div
                          key={automation.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <CommandLineIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-gray-900">
                                {automation.name}
                              </p>
                              <p className="text-sm text-gray-600">
                                {automation.description}
                              </p>
                              <p className="text-xs text-gray-500">
                                Status: {automation.status} • Type:{" "}
                                {automation.type}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleAttachAutomation(automation.id)
                            }
                            className="flex items-center space-x-1 px-3 py-2 text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
                          >
                            <LinkIcon className="h-4 w-4" />
                            <span>Attach</span>
                          </button>
                        </div>
                      ))}
                    {availableAutomations.filter(
                      (automation) =>
                        !deal.attachedAutomations?.some(
                          (attached) => attached.automationId === automation.id
                        )
                    ).length === 0 && (
                      <p className="text-gray-500 text-sm">
                        All available automations are already attached to this
                        deal.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealDetail;
